import * as React from 'react';

import { Alert, Box, LinearProgress, Stack, Typography } from '@mui/material';
import ChipSelect from 'components/ReportRequestV2/ChipSelect';
import FormSection from 'components/ReportRequestV2/FormSection';
import ProductPicker from 'components/ReportRequestV2/ProductPicker';
import SelectedProductsTable from 'components/ReportRequestV2/SelectedProductsTable';
import { uniq } from 'lodash';

import CustomClassSection from './CustomClassSection';
import { StepProps } from './types';
import { ProductCatalog } from '../hooks/useProductCatalog';
import { MIN_PRODUCT_REVIEWS } from '../model/constants';
import { originalCategoriesOf, setField } from '../model/reducer';
import {
  lowDataProductNames,
  uncategorisedProductNames,
  updateAddsNothing,
} from '../model/validation';

interface Props extends StepProps {
  catalog: ProductCatalog;
  missingProducts?: string[];
}

const MissingProductsAlert: React.FC<{ names: string[] }> = ({ names }) =>
  names.length === 0 ? null : (
    <Alert severity="warning">
      {names.length} product{names.length === 1 ? ' is' : 's are'} no longer in
      the catalog and {names.length === 1 ? 'was' : 'were'} not added:{' '}
      {names.join(', ')}
    </Alert>
  );

const LowDataAlert: React.FC<{ names: string[] }> = ({ names }) =>
  names.length === 0 ? null : (
    <Alert severity="warning">
      {names.length} product{names.length === 1 ? ' has' : 's have'} fewer than{' '}
      {MIN_PRODUCT_REVIEWS} reviews and may produce unreliable results.
    </Alert>
  );

const CompetitiveSetStep: React.FC<Props> = ({
  state,
  dispatch,
  errors,
  catalog,
  missingProducts = [],
}) => {
  const isUpdate = state.mode === 'update';
  const selectedIds = React.useMemo(
    () => new Set(state.products.map((p) => p.id)),
    [state.products],
  );

  const workspaceOptions = uniq([
    ...state.products.map((p) => p.workspace),
    ...(state.updateSource?.originalWorkspaces ?? []),
    ...state.allowedWorkspaces,
  ]).sort();
  const categoryOptions = uniq([
    ...state.products.map((p) => p.category).filter(Boolean),
    ...state.categories,
    ...originalCategoriesOf(state),
  ]).sort();
  const lowData = lowDataProductNames(state);
  const uncategorised = uncategorisedProductNames(state);
  const originalWorkspaces = state.updateSource?.originalWorkspaces ?? [];
  const originalCategories = originalCategoriesOf(state);
  const catalogPending = catalog.loading && state.products.length === 0;

  return (
    <>
      <FormSection
        title={isUpdate ? 'Products to add' : 'Products'}
        description={
          isUpdate
            ? 'Products already on the report are locked. Add new products to include them in the updated report.'
            : 'The competitive set the report compares. A product can exist in several workspaces; every workspace row is added.'
        }
      >
        <Box sx={{ display: 'grid', gap: 2 }}>
          <ProductPicker
            catalog={catalog.catalog}
            loading={catalog.loading}
            selectedIds={selectedIds}
            onAdd={(products) => dispatch({ type: 'addProducts', products })}
            onRemoveNames={(names) =>
              names.forEach((name) => dispatch({ type: 'removeProduct', name }))
            }
            defaultWorkspace={state.workspace?.name}
            allowedWorkspaces={state.allowedWorkspaces}
            error={errors.products}
          />
          {catalog.error && (
            <Alert severity="error">
              The product catalog failed to load: {catalog.error.message}
            </Alert>
          )}
          <MissingProductsAlert names={missingProducts} />
          {catalogPending ? (
            <Stack spacing={1} role="status" aria-label="Loading products">
              <LinearProgress />
              <Typography variant="body2" color="text.secondary">
                {isUpdate
                  ? "Loading the report's products…"
                  : 'Loading the product catalog…'}
              </Typography>
            </Stack>
          ) : (
            <SelectedProductsTable
              products={state.products}
              allowedWorkspaces={state.allowedWorkspaces}
              minReviews={MIN_PRODUCT_REVIEWS}
              onRemove={(name) => dispatch({ type: 'removeProduct', name })}
              onAllowWorkspace={(workspace) =>
                dispatch({ type: 'toggleAllowedWorkspace', workspace })
              }
              onClear={
                isUpdate ? undefined : () => dispatch({ type: 'clearProducts' })
              }
            />
          )}
          <LowDataAlert names={lowData} />
          {isUpdate && !catalogPending && updateAddsNothing(state) && (
            <Alert severity="info">
              No new products or workspaces yet. Submitting now re-runs the
              report with its original competitive set.
            </Alert>
          )}
        </Box>
      </FormSection>

      {state.products.length > 0 && (
        <FormSection
          title="Data sources"
          description="Which workspaces' reviews may be used, and which categories the competitive set spans."
        >
          <Box sx={{ display: 'grid', gap: 3 }}>
            <ChipSelect
              label="Allowed workspaces"
              options={workspaceOptions.map((w) => ({
                value: w,
                label: w,
                disabled: isUpdate && originalWorkspaces.includes(w),
                hint:
                  isUpdate && originalWorkspaces.includes(w)
                    ? 'Already allowed on the report'
                    : undefined,
              }))}
              selected={state.allowedWorkspaces}
              onToggle={(workspace) =>
                dispatch({ type: 'toggleAllowedWorkspace', workspace })
              }
              error={errors.allowedWorkspaces}
              helperText="AFS, CLT and the report's own workspace are allowed automatically when their products are added."
            />
            <ChipSelect
              label="Categories"
              options={categoryOptions.map((c) => ({
                value: c,
                label: c,
                disabled: originalCategories.includes(c),
                hint: originalCategories.includes(c)
                  ? 'Already on the report'
                  : undefined,
              }))}
              selected={state.categories}
              onToggle={(category) =>
                dispatch({ type: 'toggleCategory', category })
              }
              onSelectAll={() =>
                dispatch(setField('categories', categoryOptions))
              }
              onClear={() =>
                dispatch(setField('categories', originalCategories))
              }
              error={errors.categories}
              helperText={
                uncategorised.length > 0
                  ? `No category in the catalog for: ${uncategorised.join(', ')}`
                  : 'Categories drive the number of compute cores and the category norms.'
              }
            />
          </Box>
        </FormSection>
      )}

      {!isUpdate && (
        <CustomClassSection
          state={state}
          dispatch={dispatch}
          errors={errors}
          catalog={catalog}
        />
      )}
    </>
  );
};

export default CompetitiveSetStep;
